import React, {useState, useEffect} from 'react';
import {TypeImplementation, Plugins, Result} from '@packages/systems/wfdl';
import {type DesignerStores} from '@packages/systems/designer/flux';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  TTypeConstructor,
  functionOf,
  TText,
  ELiteral,
} from '@packages/systems/wfdl-ast';
import moment from 'moment-timezone';
import {
  qualify,
  pluginName,
  iconName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  View,
  VStack,
  DateIcon,
  Menu,
  MenuTrigger,
  MenuContent,
  TextInput,
} from '@packages/systems/spring';
import {SingleDatePicker} from '@packages/systems/ui/shared-ui/DatePicker';
import {emotionStyled} from '@packages/utilities/styled';
import * as DateUtils from '@packages/shared/utils/DateUtils';

import {DateTime12} from '@packages/systems/ui/icons';

import {getNumber, getString} from '@packages/utilities/json';
import {useDesignerSelector} from '@packages/systems/designer/flux';

export const TDateTime = TTypeConstructor(
  qualify(pluginName('DateTime'), typeName('DateTime'))
);

const dateTypeName = qualify(Plugins.name('DateTime'), typeName('Date'));
export const TDate = TTypeConstructor(
  qualify(pluginName('DateTime'), typeName('Date'))
);

const DateInputWrapper = emotionStyled(View)({
  display: 'flex',
  alignItems: 'stretch',
  borderRadius: '$2',
  overflow: 'hidden',
});

const DateIconButton = emotionStyled(View)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$whiteA10',
  paddingX: '$2',
});

const DateInputField = emotionStyled(TextInput)({
  flex: 1,
  borderRadius: '0 $2 $2 0',
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    display: 'none',
  },
});

const getDateString = (expr: ContextualExpression): string | undefined => {
  const dateValue =
    ContextualExpressions.getLiteral(stripAnn(expr)) || undefined;
  return dateValue?.value ? getString(dateValue.value) : undefined;
};

const selectTimezone = (state: DesignerStores) =>
  state.DataManagerUIStore.timezone || DateUtils.UTC;

const DateRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const timezone = useDesignerSelector(selectTimezone);
  const dateString = getDateString(expr);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const displayDate = dateString || moment.tz(timezone).format('YYYY-MM-DD');

  const [inputValue, setInputValue] = useState(displayDate);

  // Update display when dateString changes
  useEffect(() => {
    const newDisplayDate =
      dateString || moment.tz(timezone).format('YYYY-MM-DD');
    setInputValue(newDisplayDate);
  }, [dateString, timezone]);

  return (
    <VStack spacing={2}>
      <Menu
        isOpen={isDatePickerOpen}
        onRequestOpen={() => setIsDatePickerOpen(true)}
        onRequestClose={() => setIsDatePickerOpen(false)}
      >
        <DateInputWrapper>
          <MenuTrigger asChild>
            <DateIconButton>
              <DateIcon />
            </DateIconButton>
          </MenuTrigger>
          <DateInputField
            value={inputValue}
            type="date"
            aria-label="Exact date"
            onChange={(e) => {
              const newInputValue = e.target.value;
              setInputValue(newInputValue);
              // Store the date string directly
              if (moment(newInputValue, 'YYYY-MM-DD', true).isValid()) {
                dispatch(
                  updateExpressionAction(
                    ELiteral({
                      name: dateTypeName,
                      value: newInputValue,
                    })
                  )
                );
              }
            }}
          />
        </DateInputWrapper>
        <MenuContent>
          <SingleDatePicker
            value={
              dateString
                ? moment.tz(dateString, 'YYYY-MM-DD', timezone).toDate()
                : moment.tz(timezone).toDate()
            }
            onChange={(date) => {
              const dateStr = moment.tz(date, timezone).format('YYYY-MM-DD');
              setInputValue(dateStr);
              dispatch(
                updateExpressionAction(
                  ELiteral({
                    name: dateTypeName,
                    value: dateStr,
                  })
                )
              );
              setIsDatePickerOpen(false);
            }}
            timezone={timezone}
          />
        </MenuContent>
      </Menu>
    </VStack>
  );
};

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    DateTime: DateTime12,
  },
  dataTypes: {
    DateTime: TypeImplementation.Custom({
      parseValue: (json) => {
        const number = getNumber(json);

        if (number === undefined) {
          return Result.Error('Expected a number for DateTime.');
        }

        return Result.Ok(number);
      },

      render: function RenderDateTime() {
        return null;
      },

      icon: TypeImplementation.IconByName(
        qualify(pluginName('DateTime'), iconName('DateTime'))
      ),
    }),
    Date: TypeImplementation.Custom({
      parseValue: (json) => {
        const str = getString(json);

        if (str === undefined) {
          return Result.Error('Expected a string for Date.');
        }

        return Result.Ok(str);
      },

      render: DateRender,

      icon: TypeImplementation.IconByName(
        qualify(pluginName('DateTime'), iconName('DateTime'))
      ),
    }),
  },

  values: {
    'To Text': {
      envType: {
        dataType: functionOf(TDateTime, TText),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      value: String,
    },
  },
});
